/**
 * Escalation Rules Modal - Configure escalation rules per stage
 */

import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Clock, 
  AlertTriangle, 
  Bell, 
  UserPlus, 
  UserCheck, 
  ArrowRight, 
  CheckCircle, 
  XCircle,
  Plus,
  Trash2,
  Settings,
  Timer,
  Send,
  RefreshCw,
} from "lucide-react";

// Escalation triggers
const ESCALATION_TRIGGERS = [
  { 
    value: "no_response", 
    label: "No Response", 
    description: "When approver doesn't respond within time limit",
    icon: Clock,
    color: "text-orange-500"
  },
  { 
    value: "sla_warning", 
    label: "SLA Warning", 
    description: "When approaching SLA deadline (e.g., 80% of time elapsed)",
    icon: AlertTriangle,
    color: "text-yellow-500"
  },
  { 
    value: "sla_breach", 
    label: "SLA Breach", 
    description: "When SLA time limit is exceeded",
    icon: Timer,
    color: "text-red-500"
  },
];

// Escalation actions
const ESCALATION_ACTIONS = [
  { 
    value: "notify_approver", 
    label: "Notify Approver", 
    description: "Send notification to current approver",
    icon: Bell,
    category: "Notification"
  },
  { 
    value: "notify_escalation", 
    label: "Notify Escalation Contact", 
    description: "Send notification to designated escalation contact",
    icon: Send,
    category: "Notification"
  },
  { 
    value: "add_approver", 
    label: "Add Backup Approver", 
    description: "Add additional approver to the stage",
    icon: UserPlus,
    category: "Approver Management"
  },
  { 
    value: "replace_approver", 
    label: "Replace Approver", 
    description: "Replace current approver with backup",
    icon: RefreshCw,
    category: "Approver Management"
  },
  { 
    value: "escalate_stage", 
    label: "Escalate to Next Stage", 
    description: "Move request to next approval stage",
    icon: ArrowRight,
    category: "Stage Management"
  },
  { 
    value: "auto_approve", 
    label: "Auto Approve", 
    description: "Automatically approve the request",
    icon: CheckCircle,
    category: "Automatic Action"
  },
  { 
    value: "auto_reject", 
    label: "Auto Reject", 
    description: "Automatically reject the request",
    icon: XCircle,
    category: "Automatic Action"
  },
];

interface EscalationRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  stageId: number;
  existingRules?: any[];
  onRulesChanged?: () => void;
}

interface EscalationRule {
  id?: number;
  trigger: string;
  afterHours: number;
  action: string;
  actionParameters?: any;
}

export function EscalationRulesModal({ 
  isOpen, 
  onClose, 
  stageId, 
  existingRules = [],
  onRulesChanged,
}: EscalationRulesModalProps) {
  const [rules, setRules] = useState<EscalationRule[]>(
    existingRules.map(rule => ({
      id: rule.id,
      trigger: rule.trigger,
      afterHours: rule.afterHours,
      action: rule.action,
      actionParameters: rule.actionParameters || {},
    }))
  );
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [newRule, setNewRule] = useState<EscalationRule>({
    trigger: "",
    afterHours: 24,
    action: "",
  });

  // Queries for dropdown data
  const { data: users } = trpc.users.list.useQuery();
  const { data: roles } = trpc.roles.list.useQuery();
  const { data: groups } = trpc.groups.list.useQuery();

  useEffect(() => {
    setRules(
      existingRules.map(rule => ({
        id: rule.id,
        trigger: rule.trigger || rule.triggerType,
        afterHours: rule.afterHours ?? rule.triggerValue,
        action: rule.action || rule.actionType,
        actionParameters: rule.actionParameters || rule.actionConfig || {},
      }))
    );
  }, [existingRules, isOpen]);

  // Mutations
  const addEscalationRule = trpc.workflows.addEscalationRule.useMutation({
    onSuccess: () => {
      toast.success("Escalation rule added successfully");
      setIsAddingRule(false);
      setNewRule({ trigger: "", afterHours: 24, action: "" });
      onRulesChanged?.();
    },
    onError: (error: any) => toast.error(error.message),
  });

  const removeEscalationRule = trpc.workflows.removeEscalationRule.useMutation({
    onSuccess: () => {
      toast.success("Escalation rule removed successfully");
      onRulesChanged?.();
    },
    onError: (error: any) => toast.error(error.message),
  });

  const handleAddRule = () => {
    if (!newRule.trigger || !newRule.action) {
      toast.error("Please select both trigger and action");
      return;
    }

    const ruleToAdd = {
      stageId,
      triggerType: newRule.trigger as any,
      triggerValue: newRule.afterHours,
      actionType: newRule.action as any,
      actionConfig: newRule.actionParameters || {},
    };

    addEscalationRule.mutate(ruleToAdd);
  };

  const handleRemoveRule = async (ruleId: number) => {
    removeEscalationRule.mutate({ id: ruleId });
  };

  const getTriggerInfo = (trigger: string) => {
    return ESCALATION_TRIGGERS.find(t => t.value === trigger);
  };

  const getActionInfo = (action: string) => {
    return ESCALATION_ACTIONS.find(a => a.value === action);
  };

  const renderActionParameters = () => {
    switch (newRule.action) {
      case "notify_approver":
        return (
          <div className="text-sm text-[#6B6B6B]">
            Sends notification to the current approver(s) of this stage.
          </div>
        );

      case "notify_escalation":
        return (
          <div className="space-y-2">
            <Label>Escalation Contact</Label>
            <Select
              value={newRule.actionParameters?.contactId || ""}
              onValueChange={(value) => setNewRule({
                ...newRule,
                actionParameters: { ...newRule.actionParameters, contactId: value }
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select escalation contact..." />
              </SelectTrigger>
              <SelectContent>
                {users?.users?.map((user: any) => (
                  <SelectItem key={user.id} value={String(user.id)}>
                    {user.name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "add_approver":
      case "replace_approver":
        return (
          <div className="space-y-2">
            <Label>Backup Approver</Label>
            <Select
              value={newRule.actionParameters?.approverId || ""}
              onValueChange={(value) => setNewRule({
                ...newRule,
                actionParameters: { ...newRule.actionParameters, approverId: value }
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select backup approver..." />
              </SelectTrigger>
              <SelectContent>
                {users?.users?.map((user: any) => (
                  <SelectItem key={user.id} value={String(user.id)}>
                    {user.name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Escalation Rules</DialogTitle>
          <DialogDescription>
            Configure automatic escalation actions when approval conditions are met
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Existing Rules */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Current Rules</h3>
              <Button onClick={() => setIsAddingRule(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </Button>
            </div>

            {rules.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-[#6B6B6B]">
                  <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No escalation rules configured</p>
                  <p className="text-sm">Add rules to handle timeouts and SLA breaches</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {rules.map((rule, index) => {
                  const triggerInfo = getTriggerInfo(rule.trigger);
                  const actionInfo = getActionInfo(rule.action);
                  const TriggerIcon = triggerInfo?.icon || Clock;
                  const ActionIcon = actionInfo?.icon || Bell;

                  return (
                    <Card key={rule.id || index}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full bg-gray-100 ${triggerInfo?.color}`}>
                                <TriggerIcon className="h-4 w-4" />
                              </div>
                              <div className="h-px w-8 bg-gray-300" />
                              <div className="p-2 rounded-full bg-gray-100">
                                <ActionIcon className="h-4 w-4 text-blue-500" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline">
                                  {triggerInfo?.label || rule.trigger}
                                </Badge>
                                <span className="text-sm text-[#6B6B6B]">after</span>
                                <Badge variant="secondary">
                                  {rule.afterHours}h
                                </Badge>
                                <span className="text-sm text-[#6B6B6B]">→</span>
                                <Badge>
                                  {actionInfo?.label || rule.action}
                                </Badge>
                              </div>
                              <p className="text-sm text-[#6B6B6B]">
                                {triggerInfo?.description}
                              </p>
                              {actionInfo?.description && (
                                <p className="text-sm text-[#6B6B6B] mt-1">
                                  {actionInfo.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => rule.id && handleRemoveRule(rule.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add New Rule */}
          {isAddingRule && (
            <Card className="border-2 border-dashed border-[#5B2C93]">
              <CardHeader>
                <CardTitle className="text-base">Add New Escalation Rule</CardTitle>
                <CardDescription>
                  Define when and how to escalate requests
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  {/* Trigger Selection */}
                  <div className="space-y-2">
                    <Label>Trigger *</Label>
                    <Select
                      value={newRule.trigger}
                      onValueChange={(value) => setNewRule({ ...newRule, trigger: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select trigger..." />
                      </SelectTrigger>
                      <SelectContent>
                        {ESCALATION_TRIGGERS.map((trigger) => {
                          const Icon = trigger.icon;
                          return (
                            <SelectItem key={trigger.value} value={trigger.value}>
                              <div className="flex items-center gap-2">
                                <Icon className={`h-4 w-4 ${trigger.color}`} />
                                {trigger.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {newRule.trigger && (
                      <p className="text-xs text-[#6B6B6B]">
                        {getTriggerInfo(newRule.trigger)?.description}
                      </p>
                    )}
                  </div>

                  {/* Time Delay */}
                  <div className="space-y-2">
                    <Label>After Hours *</Label>
                    <Input
                      type="number"
                      min={1}
                      value={newRule.afterHours}
                      onChange={(e) => setNewRule({ 
                        ...newRule, 
                        afterHours: parseInt(e.target.value) || 1 
                      })}
                    />
                    <p className="text-xs text-[#6B6B6B]">
                      Time before trigger activates
                    </p>
                  </div>

                  {/* Action Selection */}
                  <div className="space-y-2">
                    <Label>Action *</Label>
                    <Select
                      value={newRule.action}
                      onValueChange={(value) => setNewRule({ 
                        ...newRule, 
                        action: value,
                        actionParameters: {} // Reset parameters when action changes
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select action..." />
                      </SelectTrigger>
                      <SelectContent>
                        {ESCALATION_ACTIONS.map((action) => {
                          const Icon = action.icon;
                          return (
                            <SelectItem key={action.value} value={action.value}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {action.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {newRule.action && (
                      <p className="text-xs text-[#6B6B6B]">
                        {getActionInfo(newRule.action)?.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Action Parameters */}
                {newRule.action && renderActionParameters()}

                {/* Action Buttons */}
                <div className="flex items-center gap-2 pt-4">
                  <Button onClick={handleAddRule} disabled={addEscalationRule.isPending}>
                    {addEscalationRule.isPending ? "Adding..." : "Add Rule"}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsAddingRule(false);
                      setNewRule({ trigger: "", afterHours: 24, action: "" });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
